import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardSignature, CheckCircle2, Info } from "lucide-react";

export default function SignProtocol() {
  const [referrer, setReferrer] = useState(null);
  const [config, setConfig] = useState(null);
  const [typed, setTyped] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [{ data: ref }, { data: cfg }] = await Promise.all([
        api.get("/referrers/me"),
        api.get("/config"),
      ]);
      if (!ref) { navigate("/onboarding/profile"); return; }
      if (ref.protocol_signed) { navigate("/dashboard"); return; }
      setReferrer(ref); setConfig(cfg);
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!accept) { toast.error("Precisa de aceitar o protocolo."); return; }
    if (typed.trim().toLowerCase() !== referrer.full_name.trim().toLowerCase()) {
      toast.error("A assinatura deve coincidir com o seu nome completo.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/referrers/sign-protocol", { typed_name: typed, accepted: true });
      toast.success("Protocolo assinado com sucesso.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (!referrer || !config) return <div className="text-[var(--ink-soft)]">A carregar…</div>;

  return (
    <div className="max-w-3xl">
      <div className="eyebrow">Passo 2 de 2</div>
      <h1 className="font-heading text-4xl mt-2 flex items-center gap-3">
        <ClipboardSignature className="w-8 h-8 text-[var(--forest)]"/> Assinatura digital do protocolo
      </h1>
      <p className="text-[var(--ink-soft)] mt-2">Versão {config.protocol_version} · Ficará registada data, hora, IP e nome assinado.</p>
      <div className="mt-3 text-sm text-[var(--ink-soft)]">
        Perfil criado com o código de referência <span className="chip chip-neutral ml-1" data-testid="referrer-code">{referrer.code}</span>
      </div>

      <div className="card-surface p-8 mt-8">
        <div className="prose prose-sm max-w-none text-[var(--ink)] whitespace-pre-wrap font-body leading-relaxed">
          {config.protocol_text}
        </div>
        <div className="mt-6 grid md:grid-cols-3 gap-3">
          <div className="rounded-xl bg-[var(--muted)] p-4">
            <div className="eyebrow">Prémio</div>
            <div className="font-heading text-2xl text-[var(--forest)] mt-1">{config.bonus_amount} €</div>
            <div className="text-xs text-[var(--ink-soft)] mt-1">por vaga preenchida</div>
          </div>
          <div className="rounded-xl bg-[var(--muted)] p-4">
            <div className="eyebrow">Condição — Continuidade</div>
            <div className="font-heading text-2xl text-[var(--ink)] mt-1">≥ {config.min_validation_days} dias</div>
            <div className="text-xs text-[var(--ink-soft)] mt-1">o candidato tem de permanecer no serviço</div>
          </div>
          <div className="rounded-xl bg-[var(--muted)] p-4">
            <div className="eyebrow">Condição — Pontual</div>
            <div className="font-heading text-2xl text-[var(--ink)] mt-1">100 %</div>
            <div className="text-xs text-[var(--ink-soft)] mt-1">o serviço tem de ser concluído na totalidade</div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="card-surface p-8 mt-6 space-y-4">
        <div className="flex items-start gap-3 p-4 rounded-xl bg-[rgba(59,113,151,0.08)] border border-[rgba(59,113,151,0.2)] text-sm">
          <Info className="w-4 h-4 text-[var(--info)] mt-0.5 flex-shrink-0"/>
          <div className="text-[var(--ink)]">Escreva o seu nome completo exatamente como aparece no seu perfil: <strong>{referrer.full_name}</strong></div>
        </div>
        <div>
          <label className="text-sm font-medium">Assinatura (nome completo)</label>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            required
            className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)] font-heading text-2xl italic text-[var(--forest)]"
            data-testid="protocol-typed-name-input"
            placeholder="O seu nome completo"
          />
        </div>
        <label className="flex items-start gap-3">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-1" data-testid="protocol-accept-checkbox"/>
          <span className="text-sm text-[var(--ink-soft)]">Declaro que li e aceito integralmente o protocolo acima, incluindo prémio, exclusões e datas de pagamento.</span>
        </label>
        <button disabled={busy} className="pill-btn pill-primary w-full justify-center" data-testid="protocol-sign-btn">
          <CheckCircle2 className="w-4 h-4"/> {busy ? "A registar…" : "Assinar e continuar"}
        </button>
      </form>
    </div>
  );
}
